import React from "react";
import { ArrowRight, MapPin } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1553024838-a90a23993a12?auto=format&fit=crop&w=2200&q=85";

export const Hero = () => {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-stone-950 pt-24"
    >
      {/* Background image (right side, fades to dark on left) */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Spices and herbs in moody light"
          className="absolute inset-0 w-full h-full object-cover kenburns"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/85 to-stone-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-transparent" />
      </div>

      {/* Left vertical slogan rail */}
      <div
        className="hidden lg:flex absolute left-6 xl:left-10 top-0 bottom-0 z-10 items-center pointer-events-none"
        aria-hidden="true"
      >
        <span
          data-testid="hero-slogan"
          className="font-proxima text-orange-500 text-[10px] xl:text-xs uppercase tracking-[0.42em] font-semibold whitespace-nowrap"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Authentic Flavours&nbsp;&nbsp;·&nbsp;&nbsp;Cultural Roots&nbsp;&nbsp;·&nbsp;&nbsp;Elevated Experience
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-20 xl:pl-28 lg:pr-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[calc(100svh-6rem)]">
        <div className="lg:col-span-8 xl:col-span-7 py-20">
          {/* Overline */}
          <div
            className="reveal flex items-center gap-3 text-orange-500 font-sans-display text-xs uppercase tracking-[0.32em] mb-8"
            data-testid="hero-overline"
          >
            <span className="h-px w-10 bg-orange-500" />
            <span>Authentic Kitchen — Coming Soon</span>
          </div>

          {/* Headline */}
          <h1
            className="reveal reveal-delay-1 font-serif-display text-stone-50 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight"
            data-testid="hero-headline"
          >
            Something <em className="not-italic text-orange-500">Bold</em>
            <br />
            is Coming.
          </h1>

          {/* Tagline */}
          <p
            className="reveal reveal-delay-2 mt-6 font-serif-display italic text-stone-300 text-2xl sm:text-3xl"
            data-testid="hero-tagline"
          >
            Embrace your identity. Experience the world through food.
          </p>

          {/* Mobile slogan (visible below lg) */}
          <p
            className="reveal reveal-delay-2 lg:hidden mt-4 font-proxima text-orange-500 text-[10px] uppercase tracking-[0.22em] font-semibold"
            data-testid="hero-slogan-mobile"
          >
            Authentic Flavours · Cultural Roots · Elevated Experience.
          </p>

          {/* Subtext */}
          <p
            className="reveal reveal-delay-3 mt-8 max-w-xl text-stone-300 text-base sm:text-lg leading-relaxed font-sans-display"
            data-testid="hero-subtext"
          >
            A powerful fusion of culture, flavour, and identity — all in one
            place. From healthy snacks to bar bites and full-course meals,
            we&apos;re redefining how you experience food.
          </p>

          {/* Location pill */}
          <div
            className="reveal reveal-delay-3 mt-8 inline-flex items-center gap-2 border border-stone-700 px-4 py-2 text-stone-300 font-sans-display text-xs uppercase tracking-[0.24em]"
            data-testid="hero-location"
          >
            <MapPin className="h-3.5 w-3.5 text-orange-500" />
            Launching in the GTA
          </div>

          {/* CTAs */}
          <div className="reveal reveal-delay-4 mt-12 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <a
              href="#flavours"
              onClick={scrollTo("flavours")}
              data-testid="hero-cta-explore"
              className="group inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-stone-50 font-proxima uppercase tracking-[0.22em] text-xs font-semibold px-8 py-5 transition-colors"
            >
              Explore the Flavours
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>

        {/* Right rail vertical text */}
        <div
          className="hidden lg:flex lg:col-span-4 xl:col-span-5 justify-end items-end h-full pb-16"
          aria-hidden="true"
        >
          <div className="reveal reveal-delay-4 flex flex-col items-end gap-6">
            <span className="font-serif-display italic text-stone-500 text-sm">
              No.&nbsp;01
            </span>
            <span
              className="font-sans-display text-[10px] uppercase tracking-[0.5em] text-stone-500"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
            >
              Culture · Flavour · Identity
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
